import { useState, type Ref } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { Handshake, ShoppingCart } from 'lucide-react';
import { EmptyState } from '~/components/ui/EmptyState';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';
import { Table, type TableColumn, type TableRow } from '~/components/ui/table';
import { statusBadgeClasses } from '~/components/ui/statusBadgeClasses';
import { formatCurrency, formatDate, sellerLabel } from '~/utils';
import type { TradePerspective } from '~/graphql/graphql';
import { PriceNegotiationStatus } from '~/graphql/graphql';
import { orderBuilderUrl } from '~/pages/price-negotiations/orderBuilderLink';
import {
  priceNegotiationProduct,
  type PriceNegotiationRow,
} from '~/pages/price-negotiations/priceNegotiationProduct';
import { RespondToNegotiationDialog } from '~/pages/price-negotiations/sections/RespondToNegotiationDialog';

type PriceNegotiationListProps = {
  perspective: TradePerspective;
  negotiations: PriceNegotiationRow[];
  isLoading: boolean;
  isError: boolean;
  emptyMessage: string;
  sentinelRef?: Ref<HTMLDivElement>;
  isLoadingMore?: boolean;
  hasMoreData?: boolean | null;
  totalCount?: number;
  showResponseColumns?: boolean;
  showRespondAction?: boolean;
  showBuildOrderAction?: boolean;
  onResponded?: (negotiationId: string) => void;
};

function statusBadgeClassName(status: PriceNegotiationStatus): string {
  switch (status) {
    case PriceNegotiationStatus.Accepted:
      return `${statusBadgeClasses('accepted')} whitespace-nowrap`;
    case PriceNegotiationStatus.Declined:
      return `${statusBadgeClasses('declined')} whitespace-nowrap`;
    default:
      return `${statusBadgeClasses('pending')} whitespace-nowrap`;
  }
}

function ProductCell({
  negotiation,
  t,
}: Readonly<{ negotiation: PriceNegotiationRow; t: TFunction }>) {
  const { name, lengthLabel, code } = priceNegotiationProduct(negotiation);
  const isTally = negotiation.platformProductCannedTally != null;
  const fallbackName = isTally
    ? t('price_negotiations.tally_product')
    : t('price_negotiations.length_product');

  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="text-neut-900 font-medium">
          {name ?? fallbackName}
        </span>
        {lengthLabel && (
          <span className="shrink-0 whitespace-nowrap rounded bg-success-100 px-2 py-0.5 text-xs font-medium text-success-700">
            {lengthLabel}
          </span>
        )}
        {isTally && (
          <span className="shrink-0 whitespace-nowrap rounded bg-neut-100 px-2 py-0.5 text-xs font-medium text-neut-600">
            {t('price_negotiations.tally_product')}
          </span>
        )}
      </div>
      {code && <div className="text-xs text-neut-500">{code}</div>}
    </div>
  );
}

function priceLabel(
  price: { amount: number; currency: string } | null | undefined,
  unit: string
): string {
  if (!price) return '-';
  return `${formatCurrency(price.amount, price.currency)}/${unit}`;
}

export function PriceNegotiationList({
  perspective,
  negotiations,
  isLoading,
  isError,
  emptyMessage,
  sentinelRef,
  isLoadingMore,
  hasMoreData,
  totalCount,
  showResponseColumns = true,
  showRespondAction = false,
  showBuildOrderAction = false,
  onResponded,
}: Readonly<PriceNegotiationListProps>) {
  const { t } = useTranslation('price-negotiations');
  const returnPath = useLocation().pathname;
  const [respondingNegotiation, setRespondingNegotiation] =
    useState<PriceNegotiationRow | null>(null);
  const isSelling = perspective === 'SELLING';

  if (isLoading) {
    return <PageLoadingSpinner name={t('price_negotiations.title')} />;
  }

  if (isError) {
    return (
      <div
        data-testid="price-negotiations-error"
        role="alert"
        className="text-sm text-error"
      >
        {t('price_negotiations.load_error')}
      </div>
    );
  }

  if (negotiations.length === 0) {
    return (
      <EmptyState
        icon={<Handshake size={48} />}
        title={t('price_negotiations.no_negotiations_title')}
        message={emptyMessage}
      />
    );
  }

  const counterparty = (negotiation: PriceNegotiationRow): string => {
    if (isSelling) {
      const requester = negotiation.createdBy
        ? `${negotiation.createdBy.firstName} ${negotiation.createdBy.lastName}`.trim()
        : '';
      const organization = negotiation.buyingOrganization?.name ?? '';
      return [organization, requester].filter(Boolean).join(' · ');
    }
    return negotiation.sellingOrganization
      ? sellerLabel(negotiation.sellingOrganization)
      : '';
  };

  const counterpartyHeader = t(
    isSelling
      ? 'price_negotiations.columns.buying_organization'
      : 'price_negotiations.columns.selling_organization'
  );

  const columns: TableColumn[] = [
    {
      key: 'product',
      header: t('price_negotiations.columns.product'),
      width: 16,
      stickTo: 'left',
    },
    {
      key: 'counterparty',
      header: counterpartyHeader,
      width: 14,
    },
    {
      key: 'targetPrice',
      header: t('price_negotiations.columns.target_price'),
      width: 8,
      justification: 'end',
    },
    ...(showResponseColumns
      ? [
          {
            key: 'offeredPrice',
            header: t('price_negotiations.columns.offered_price'),
            width: 8,
            justification: 'end' as const,
          },
        ]
      : []),
    { key: 'status', header: t('price_negotiations.columns.status'), width: 7 },
    {
      key: 'updatedAt',
      header: t('price_negotiations.columns.updated_at'),
      width: 8,
    },
  ];

  const showActionsColumn = isSelling
    ? showRespondAction
    : showBuildOrderAction;
  if (showActionsColumn) {
    columns.push({
      key: 'actions',
      header: t('price_negotiations.columns.actions'),
      width: 10,
      justification: 'end',
      stickTo: 'right',
    });
  }

  function actionsCell(negotiation: PriceNegotiationRow) {
    if (isSelling) {
      if (!showRespondAction) return '';
      if (negotiation.status !== PriceNegotiationStatus.Pending) return '';
      return (
        <button
          type="button"
          onClick={() => setRespondingNegotiation(negotiation)}
          data-testid={`price-negotiation-respond-${negotiation.id}`}
          className="px-4 py-2 text-sm font-medium text-neut-0 bg-prim rounded-lg hover:bg-prim-600 transition-colors whitespace-nowrap"
        >
          {t('price_negotiations.respond')}
        </button>
      );
    }

    if (!showBuildOrderAction) return '';
    if (negotiation.status !== PriceNegotiationStatus.Accepted) return '';
    const builderUrl = orderBuilderUrl(negotiation, returnPath);
    if (!builderUrl) return '';
    return (
      <Link
        to={builderUrl}
        data-testid={`price-negotiation-order-builder-${negotiation.id}`}
        className="flex items-center gap-2 px-3 py-1.5 border border-prim text-prim text-xs font-medium rounded-lg hover:bg-emerald-50 active:bg-emerald-100 transition-colors whitespace-nowrap"
      >
        <ShoppingCart size={16} aria-hidden="true" />
        {t('price_negotiations.build_order')}
      </Link>
    );
  }

  const rows: TableRow[] = negotiations.map((negotiation) => ({
    id: { value: negotiation.id },
    product: { value: <ProductCell negotiation={negotiation} t={t} /> },
    counterparty: {
      value: (
        <div>
          <div>{counterparty(negotiation)}</div>
          {isSelling && negotiation.recipients.length > 0 ? (
            <div className="text-xs text-neut-500">
              {t('price_negotiations.assigned_to', {
                names: negotiation.recipients
                  .map((recipient) =>
                    `${recipient.firstName} ${recipient.lastName}`.trim()
                  )
                  .join(', '),
              })}
            </div>
          ) : null}
        </div>
      ),
    },
    targetPrice: {
      value: priceLabel(negotiation.targetPrice, negotiation.unit),
    },
    offeredPrice: {
      value: priceLabel(negotiation.offeredPrice, negotiation.unit),
    },
    status: {
      value: (
        <span className={statusBadgeClassName(negotiation.status)}>
          {t(`price_negotiations.status.${negotiation.status}`)}
        </span>
      ),
    },
    updatedAt: {
      value: negotiation.updatedAt ? formatDate(negotiation.updatedAt) : '-',
    },
    actions: { value: actionsCell(negotiation) },
  }));

  return (
    <div
      data-testid="price-negotiations-table"
      className="mt-8 rounded-lg border border-neut-border overflow-hidden"
    >
      <Table
        rows={rows}
        columns={columns}
        minWidth={64}
        totalRowsCount={totalCount}
        name={t('price_negotiations.items')}
      />
      {isLoadingMore && (
        <div className="flex justify-center py-4">
          <div className="text-sm text-neut-500">
            {t('price_negotiations.loading_more')}
          </div>
        </div>
      )}
      {hasMoreData === false && !isLoading && (
        <div className="flex justify-center py-4">
          <div className="text-sm text-neut-500">
            {t('price_negotiations.no_more_items')}
          </div>
        </div>
      )}
      <div
        ref={sentinelRef}
        id="scroll-sentinel"
        style={{ height: '1px', background: 'transparent' }}
        data-testid="scroll-sentinel"
      />
      {respondingNegotiation && (
        <RespondToNegotiationDialog
          negotiation={respondingNegotiation}
          onClose={() => setRespondingNegotiation(null)}
          onResponded={(negotiationId) => onResponded?.(negotiationId)}
        />
      )}
    </div>
  );
}
